import { subDays } from "date-fns";
import equal from "fast-deep-equal/es6";
import { JSONSchema7Object } from "json-schema";

import {
    AiStructureGenerationResponse,
    CoreSystemMessage,
    ReasoningEffort,
    ReasoningModels,
} from "@formant/core/client/AiClient";
import type { IGatewayToken } from "@formant/core/client/GatewayClient";
import {
    ICreateTheopolisTaskRequest,
    ICreateTheopolisTaskResultFeedbackRequest,
    ICreateTheopolisTaskResultRequest,
    ICreateTheopolisTaskSourceRequest,
    IPatchTheopolisTaskRequest,
    IPatchTheopolisTaskResultFeedbackRequest,
    IPersona,
    ITheopolisTask,
    ITheopolisTaskResult,
    ITheopolisTaskResultFeedback,
    ITheopolisTaskSource,
    IUpdateTheopolisTaskSourceRequest,
} from "@formant/core/client/TheopolisClient";
import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { serializeHash } from "@formant/core/common/serializeHash";
import { AnalyticsAggregateType } from "@formant/core/model/AnalyticsAggregateType";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IDocumentSet } from "@formant/core/model/documents/IDocumentSet";
import { HasId } from "@formant/core/model/HasId";
import { IAggregateTimeRange } from "@formant/core/model/IAggregateTimeRange";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IAnnotationTemplate } from "@formant/core/model/IAnnotationTemplate";
import { IBatteryAggregate } from "@formant/core/model/IBatteryAggregate";
import { IBitsetAggregate } from "@formant/core/model/IBitsetAggregate";
import { ICommand } from "@formant/core/model/ICommand";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { IComment } from "@formant/core/model/IComment";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventCountsByDevice } from "@formant/core/model/IEventCountsByDevice";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IEventQuery } from "@formant/core/model/IEventQuery";
import { IEventSeekQuery } from "@formant/core/model/IEventSeekQuery";
import { IFilter } from "@formant/core/model/IFilter";
import { IIntegration } from "@formant/core/model/IIntegration";
import { IWorkflowAction } from "@formant/core/model/IIntelligenceGraph";
import { IIntervalQuery } from "@formant/core/model/IIntervalQuery";
import { IInterventionRequest } from "@formant/core/model/IInterventionRequest";
import { IInterventionResponse } from "@formant/core/model/IInterventionResponse";
import { IKeyring } from "@formant/core/model/IKeyring";
import { IKeyringFilter } from "@formant/core/model/IKeyringFilter";
import { IKeyringLogin } from "@formant/core/model/IKeyringLogin";
import { IKeyringLoginResponse } from "@formant/core/model/IKeyringLoginResponse";
import { IKeyringLogout } from "@formant/core/model/IKeyringLogout";
import { IListResponse } from "@formant/core/model/IListResponse";
import { IMetadataWithCurrentValue } from "@formant/core/model/IMetadataWithCurrentValue";
import { IModule } from "@formant/core/model/IModule";
import { IModuleConfiguration } from "@formant/core/model/IModuleConfiguration";
import {
    INode,
    INodeGraph,
    INodeGraphOutput,
    NodeInputValueSet,
} from "@formant/core/model/IModuleNode";
import { INumericAggregate } from "@formant/core/model/INumericAggregate";
import { IQuery } from "@formant/core/model/IQuery";
import { ISchedule } from "@formant/core/model/ISchedule";
import { ISeekQuery } from "@formant/core/model/ISeekQuery";
import { ISeekResult } from "@formant/core/model/ISeekResult";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { ISqlResult } from "@formant/core/model/ISqlResult";
import { ISqlTable } from "@formant/core/model/ISqlTable";
import { IStreamAggregateData } from "@formant/core/model/IStreamAggregateData";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { IStreamCurrentValue } from "@formant/core/model/IStreamCurrentValue";
import { IStreamCurrentValueQuery } from "@formant/core/model/IStreamCurrentValueQuery";
import { IStreamData } from "@formant/core/model/IStreamData";
import { IStreamTypeMap } from "@formant/core/model/IStreamTypeMap";
import { ISystemEvent } from "@formant/core/model/ISystemEvent";
import { ITags } from "@formant/core/model/ITags";
import { ITagSets } from "@formant/core/model/ITagSets";
import { IUser } from "@formant/core/model/IUser";
import { IView } from "@formant/core/model/IView";
import { IWorkflowWebRagResponse } from "@formant/core/model/IWorkflowWebRagResponse";
import { IWorkflowWebWorkflowShallow } from "@formant/core/model/IWorkflowWebWorkflow";
import { NodeGraph } from "@formant/core/model/ModuleNodes/NodeGraph";
import { NodeRegistry } from "@formant/core/model/ModuleNodes/NodeRegistry";
import { PlaceholderNode } from "@formant/core/model/ModuleNodes/PlaceholderNode";
import { StreamType } from "@formant/core/model/StreamType";
import {
    Authentication,
    RealtimeListener,
    RealtimeMessage,
    Uuid,
} from "@formant/data-sdk";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { RealtimeDevice, RealtimeManager } from "@formant/rectangle-sdk";
import { OnlineDevicesStore } from "@formant/rectangle-sdk/src/store/OnlineDevicesStore";
import {
    DataDependencyCallback,
    IModuleClient,
    IModuleDeviceContext,
    IModuleTime,
    IProcessingState,
} from "@formant/rectangle-sdk/src/types/IModuleClient";
import {
    calculateDateRange,
    getAggregateSpan,
    getDefaultAggregateSpan,
} from "@formant/rectangle-sdk/src/utils/helpers/data.helpers";

import {
    aiClient,
    eventClient,
    gatewayClient,
    integrationClient,
    interventionRequestClient,
    interventionResponseClient,
    keyringClient,
    keyValueClient,
    onlineDevicesClient,
    seekClient,
    shareClient,
    streamClient,
    systemEventClient,
    theopolisClient,
    workflowClient,
} from "../../clients";
import { IPresenceInterval } from "../../store/IPresenceInterval";
import {
    analyticsStore,
    annotationStore,
    annotationTemplateStore,
    authStore,
    commandStore,
    commandTemplateStore,
    commentStore,
    customModuleStore,
    deviceConfigurationStore,
    deviceStore,
    documentSetStore,
    eventStore,
    metadataStore,
    presenceStore,
    queryStore,
    scheduleStore,
    streamCurrentValueStore,
    userStore,
    viewStore,
} from "../../stores";

const integrationCache = new Map<string, object | "pending">();
export const liveTimeShift = 0;

export function filterLatestStreams(
    data: IStreamCurrentValue[],
    endDate: Date,
): IStreamCurrentValue[] {
    // Use a Map to store the latest record for each unique key (combination of streamName and streamType)
    const latestMap = new Map<string, IStreamCurrentValue>();

    data.filter(item => {
        return new Date(item.currentValueTime) <= endDate;
    }).forEach(item => {
        // Create a unique key based on streamName and streamType
        const key = `${item.streamName}|${item.streamType}|${item.deviceId}`;
        const currentStored = latestMap.get(key);

        // If no record exists for this key, or the current item is more recent, update the map
        if (
            !currentStored ||
            new Date(item.currentValueTime) >
                new Date(currentStored.currentValueTime)
        ) {
            latestMap.set(key, item);
        }
    });

    // Return an array of the latest stream records
    return Array.from(latestMap.values());
}

// Register UI node types added in @formant/core 1.7.4+ that may not be in the
// version bundled with telemetry. This ensures NodeGraph can instantiate them.
if (!NodeRegistry.nodes["ui-ros-diagnostic-message-viewer"]) {
    NodeRegistry.registerModifier(
        new PlaceholderNode({
            type: "ui-ros-diagnostic-message-viewer",
            description: "a ROS diagnostic message viewer widget",
            attributes: {
                stream: {
                    description: "JSON stream name",
                    schema: { type: "string" },
                    default: "",
                },
                fullScreenMode: {
                    description: "Realtime mode",
                    schema: { type: "string" },
                    default: "false",
                },
            },
            inputs: {
                value: {
                    type: "number",
                    description: "value",
                },
            },
        }),
    );
}

export class FormantWebModuleClient implements IModuleClient {
    processingIsLive = false;
    processingIsPlaying = false;
    processingTime: IModuleTime | undefined;
    processingDeviceContext: IModuleDeviceContext | undefined;
    processingVisibleTimelineTimeRange: { start: Date; end: Date } | undefined;
    processingTags: ITagSets | ITagSets[] = {};
    processingPausedAtTime: IModuleTime | undefined;
    processingAggregateTimeRange: IAggregateTimeRange | undefined;
    processingFilterOnlyByTags: boolean = false;
    processingGlobalFilter: IFilter | undefined;

    private deviceContext: IModuleDeviceContext | undefined;
    private realtimeManager = new RealtimeManager();
    private initialAuth = false;
    private onlineDevicesStore = new OnlineDevicesStore(
        onlineDevicesClient as any,
        async () => {
            return authStore.token();
        },
    );
    private registeredRealtimeNodeGraphs: NodeGraph[] = [];
    private registerRealtimeGraphTimeout: NodeJS.Timeout | undefined;
    private extraTags: ITagSets = {};

    async createLongLifeToken(
        deviceIds: string[],
        dashboardModuleId: string,
        userName: string,
        moduleName: string,
        experationDate: Date,
    ): Promise<string | undefined> {
        const token = await authStore.token();
        const res = await shareClient.create(token, {
            message: moduleName,
            userName,
            dashboardModuleId,
            delegateTeleop: false,
            expiration: experationDate?.toISOString() || undefined,
            time: new Date().toISOString(),
            scope: {
                deviceIds,
            },
        });
        if (!res.code) {
            throw new Error("Failed to create share");
        }
        return await shareClient.authenticate(res.code);
    }

    setProcessingState(state: IProcessingState): void {
        this.deviceContext = state.deviceContext;

        if (state.isPlaying === true) {
            this.processingPausedAtTime = undefined;
        } else if (
            state.isPlaying === false &&
            this.processingIsPlaying === true
        ) {
            this.processingPausedAtTime = this.processingTime;
        } else if (
            state.isPlaying === false &&
            this.processingIsPlaying === false &&
            !equal(this.processingTime, state.time)
        ) {
            this.processingPausedAtTime = state.time;
        }

        // if we are not filtering only by tags, and we have devices,
        // then we can process the device context
        if (
            state.deviceContext.type === "devices" &&
            state.deviceContext.deviceIds.length > 0 &&
            !state.filterOnlyByTags
        ) {
            this.processingDeviceContext = state.deviceContext;
        } else {
            this.processingDeviceContext = undefined;
        }

        this.processingAggregateTimeRange = state.aggregateTimeRange;
        this.processingIsPlaying = state.isPlaying;
        this.processingIsLive = state.isLive;
        this.processingTime = state.time;
        this.processingVisibleTimelineTimeRange =
            state.visibleTimelineTimeRange;
        this.processingTags = state.tags;
        this.extraTags = state.extraTags || {};
        this.processingFilterOnlyByTags = state.filterOnlyByTags;
        this.processingGlobalFilter = state.globalFilter;
    }

    async getDeviceName(deviceId: string): Promise<string> {
        const device = await deviceStore.getAsync(deviceId);
        if (device) {
            return device.name;
        }
        return deviceId;
    }

    async getOnlineDevices(): Promise<string[]> {
        return await this.onlineDevicesStore.getOnlineDevices();
    }

    navigateToEventUrl(event: IEvent): string {
        const hashParameters = {
            eventSidebar: "open",
            selectedEventId: event.id,
            time: parseIsoDate(event.time),
            mode: "pause",
        };
        return `/devices/${event.deviceId}#${serializeHash(hashParameters)}`;
    }

    async getDeviceConfig(deviceId: string): Promise<IDeviceConfiguration> {
        if (!this.initialAuth) {
            await this.handleAuthentication();
        }

        const device = await deviceStore.fetchGet(deviceId);
        if (device && device.desiredConfigurationVersion) {
            const deviceConfig = await deviceConfigurationStore.fetchGet(
                deviceId,
                device.desiredConfigurationVersion,
            );
            if (deviceConfig) {
                return deviceConfig;
            }
            throw new Error("Device config not found");
        }
        throw new Error("Device not found");
    }

    async queryCurrentValues(
        filter: IStreamCurrentValueQuery,
    ): Promise<IStreamCurrentValue<any>[]> {
        const data = await streamCurrentValueStore.fetch(filter);
        if (data === undefined) {
            return [];
        }
        return data;
    }

    async sqlQuery(query: ISqlQuery): Promise<ISqlResult> {
        query.tags = query.tags || {};
        if (this.processingFilterOnlyByTags) {
            delete query.deviceIds;
            try {
                query.tags = JSON.parse(JSON.stringify(this.processingTags));
            } catch (e) {
                console.error("Failed to parse processingTags", e);
            }
        }

        if (Object.keys(this.extraTags).length) {
            if (Array.isArray(query.tags)) {
                query.tags.push(this.extraTags);
            } else {
                query.tags = { ...query.tags, ...this.extraTags };
            }
        }
        const result = await analyticsStore.realListMatching({
            ...query,
            type: query.type || "advanced",
        });
        if (result === undefined || result === false) {
            return {
                rows: [],
                columns: [],
                sqlText: "",
                rowCount: 0,
            } as ISqlResult;
        }
        return result;
    }

    async listSqlTables(): Promise<ISqlTable[]> {
        return (await analyticsStore.listSqlTables()) || [];
    }

    async listStreamsByType(streamType: string): Promise<string[]> {
        const token = await authStore.token();
        const allStreams = await streamClient.list(token);
        return allStreams
            .filter(s => s.streamType === streamType)
            .map(s => s.streamName);
    }

    async getAuthToken(): Promise<string | undefined> {
        return await authStore.token();
    }

    async getCustomModuleConfig(
        moduleId: string,
    ): Promise<IModuleConfiguration | undefined> {
        return await customModuleStore.getConfiguration(moduleId);
    }

    async setCustomModuleConfig(
        id: string,
        configuration: string,
    ): Promise<IModuleConfiguration | undefined> {
        return await customModuleStore.upsertConfiguration({
            configuration,
            id,
        });
    }

    async createCustomModuleConfig(
        configuration: string,
    ): Promise<IModuleConfiguration> {
        return await customModuleStore.createConfiguration({
            configuration,
        });
    }

    async queryDataPoints(
        query: IQuery,
    ): Promise<IStreamData<keyof IStreamTypeMap>[]> {
        const data = await this.experimental.queryStore.queryAsync(query);

        if (data === undefined) {
            return [];
        }

        return data;
    }

    async queryMetadata(_query: IQuery): Promise<IMetadataWithCurrentValue[]> {
        const data = await this.experimental.metadataStore.listAsync(_query);
        if (data === undefined) {
            return [];
        }

        return data;
    }

    async queryMetadataWithCurrentValue(
        metadataId: string,
        rangeDays?: number,
    ): Promise<IMetadataWithCurrentValue | undefined> {
        const data =
            await this.experimental.metadataStore.getLatestValueForMetadataIdAsync(
                metadataId,
                rangeDays,
            );

        return data;
    }

    async queryEvents(query: IEventQuery): Promise<IEvent[]> {
        return this.experimental.eventStore.queryOrFetch(query);
    }

    async getEvent(eventId: string): Promise<IEvent | false> {
        return this.experimental.eventStore.fetchGet(eventId);
    }

    registerDataDependencyGraph(
        graphDefinition: INodeGraph,
        subscriber: DataDependencyCallback,
        preventAggregateSpan?: boolean,
        maxPointsPerStream?: number,
    ): () => void {
        const graph = new NodeGraph(graphDefinition);

        // collect all node graphs prior to initializing realtime graph
        const isRealtimeGraph = graph.sourceNodes.some(
            n => n.type === "realtime-stream",
        );
        if (isRealtimeGraph) {
            this.registeredRealtimeNodeGraphs.push(graph);
            clearTimeout(this.registerRealtimeGraphTimeout);
            this.registerRealtimeGraphTimeout = setTimeout(() => {
                this.initializeRealtimeGraph().catch(e => {
                    console.error("Failed to initialize realtime graph", e);
                });
            }, 250);
        }

        let paused = false;
        let lastKnownTimeRange: { start: Date; end: Date } | undefined;
        let lastKnownAggregateTimeRange: IAggregateTimeRange | undefined;
        let lastKnownFilterByTags: boolean | undefined;
        let lastKnownExtraTags: ITagSets | undefined;
        let lastKnownGlobalFilter: IFilter | undefined;
        let firstTime = true;
        let stop = false;
        const process = async (forceProcess: boolean) => {
            let pauseChanged = false;
            if (
                !equal(
                    this.processingVisibleTimelineTimeRange,
                    lastKnownTimeRange,
                ) ||
                !equal(
                    this.processingAggregateTimeRange,
                    lastKnownAggregateTimeRange,
                ) ||
                !equal(this.extraTags, lastKnownExtraTags) ||
                !equal(
                    this.processingFilterOnlyByTags,
                    lastKnownFilterByTags,
                ) ||
                !equal(this.processingGlobalFilter, lastKnownGlobalFilter)
            ) {
                lastKnownTimeRange = this.processingVisibleTimelineTimeRange;
                lastKnownAggregateTimeRange = this.processingAggregateTimeRange;
                lastKnownFilterByTags = this.processingFilterOnlyByTags;
                lastKnownExtraTags = this.extraTags;
                lastKnownGlobalFilter = this.processingGlobalFilter;
                pauseChanged = true;
            }

            if (pauseChanged) {
                // allow through once
            } else if (paused && this.processingIsPlaying) {
                paused = false;
            } else if (paused) {
                return;
            }

            let canDoRequest = true;

            const deviceIds: string[] = [];
            let tags = this.processingFilterOnlyByTags
                ? JSON.parse(JSON.stringify(this.processingTags))
                : {};
            if (
                this.processingDeviceContext &&
                this.processingDeviceContext.type === "devices"
            ) {
                if (!this.processingFilterOnlyByTags) {
                    deviceIds.push(...this.processingDeviceContext.deviceIds);
                }
            } else if (!tags || !Object.keys(tags).length) {
                canDoRequest = false;
            }

            if (Object.keys(this.extraTags).length) {
                if (Array.isArray(tags)) {
                    tags.push(this.extraTags);
                    tags = Array.from(
                        new Set(tags.map(item => JSON.stringify(item))),
                    ).map(item => JSON.parse(item));
                } else {
                    tags = { ...tags, ...this.extraTags };
                }
            }

            let end = new Date();
            let start: Date;
            let currentTime;

            if (this.processingIsLive) {
                end.setSeconds(end.getSeconds() - liveTimeShift);
            }
            start = new Date(end.getTime() - 1000 * 60);

            if (this.processingTime instanceof Date) {
                currentTime = this.processingTime;
                end = new Date(
                    defined(this.processingVisibleTimelineTimeRange).end,
                );
                if (this.processingIsLive) {
                    end.setSeconds(end.getSeconds() - liveTimeShift);
                }
                start = new Date(
                    defined(this.processingVisibleTimelineTimeRange).start,
                );
            } else {
                end = new Date(defined(this.processingTime).end);
                if (this.processingIsLive) {
                    end.setSeconds(end.getSeconds() - liveTimeShift);
                }
                start = new Date(defined(this.processingTime).start);
                currentTime = end;
            }

            // TODO: interval should be driven by the context
            const graphContext = {
                range: end.getTime() - start.getTime(),
                interval: "none" as number | "none",
                realtime: false,
            };
            // if range > 3 day, set interval to 1 hour
            if (graphContext.range > 1000 * 60 * 60 * 24 * 3) {
                graphContext.interval = 1000 * 60 * 60;
            }
            // if range >= 6hours , set interval to 1 hours
            else if (graphContext.range >= 1000 * 60 * 60 * 6) {
                graphContext.interval = 1000 * 60 * 60;
            }

            const enabledSourceNodes = graph.getEnabledInputNodes(graphContext);

            enabledSourceNodes.forEach(srcNode => {
                if (srcNode.type === "realtime-stream" && !srcNode.override) {
                    // will be fed via RTC client once connected
                    srcNode.override = [];
                }
            });

            const timelineTimes = graph.sourceNodes.filter(
                n => n.type === "timeline-time",
            );
            let usesModuleTimeRange = false;
            timelineTimes.forEach(_ => {
                const localTimeRange = _.getAttribute("localTimeRange");
                if (
                    localTimeRange &&
                    (typeof localTimeRange === "string" ||
                        typeof localTimeRange === "number") &&
                    this.processingTime instanceof Date
                ) {
                    const timeRange = calculateDateRange(
                        localTimeRange,
                        this.processingTime,
                    );
                    start = timeRange[0];
                    end = timeRange[1];
                    usesModuleTimeRange = true;
                }
                _.override = [
                    {
                        time: defined(this.processingTime),
                        isPlaying: defined(this.processingIsPlaying),
                        dateRange: {
                            start: start.getTime(),
                            end: end.getTime() - liveTimeShift,
                        },
                    },
                ];
            });

            const timeFrameNodes = graph.sourceNodes.filter(
                n => n.type === "module-time-frame",
            );

            let {
                start: moduleStartTime,
                end: moduleEndTime,
                aggregateLevel,
            } = this.processingAggregateTimeRange || {
                start: subDays(new Date(), 30).toISOString(),
                end: new Date().toISOString(),
                aggregateLevel: "date",
                timescale: "1m",
            };

            timeFrameNodes.forEach(_ => {
                if (!_.getAttribute("useGlobalAggregateTimeSettings")) {
                    moduleStartTime = _.getAttribute("start") as string;
                    moduleEndTime = _.getAttribute("end") as string;
                }

                aggregateLevel = _.getAttribute("aggregateLevel") as string;

                _.override = [
                    {
                        time: defined(this.processingTime),
                        isPlaying: defined(this.processingIsPlaying),
                    },
                ];
            });

            // A module with its own time range is bucketed for that range, not
            // the view's: a 30 second module in a 3 hour view would otherwise
            // collapse into a single one-minute bucket.
            const aggregateSpanStart =
                !usesModuleTimeRange &&
                this.processingVisibleTimelineTimeRange?.start
                    ? new Date(this.processingVisibleTimelineTimeRange.start)
                    : start;
            const aggregateSpanEnd =
                !usesModuleTimeRange &&
                this.processingVisibleTimelineTimeRange?.end
                    ? new Date(this.processingVisibleTimelineTimeRange.end)
                    : end;

            const aggregateSpan = !preventAggregateSpan
                ? getAggregateSpan([aggregateSpanStart, aggregateSpanEnd])
                : false;

            const streamAggregatesNodes = graph.sourceNodes.filter(
                n => n.type === "device-stream-aggregates",
            );

            for (const _ of streamAggregatesNodes) {
                const streamName = _.getAttribute("streamName") as string;
                const streamType = _.getAttribute("streamType") as string;
                const path = _.getAttribute("path") as string;
                const aggregateType = _.getAttribute(
                    "aggregateType",
                ) as AnalyticsAggregateType;

                if (streamName === null || streamName === undefined) {
                    return;
                }

                let result: ISqlResult & {
                    metadata?: {
                        start: string;
                        end: string;
                        aggregateLevel: string;
                    };
                };

                if (canDoRequest) {
                    result = await this.sqlQuery({
                        deviceIds: deviceIds.length ? deviceIds : undefined,
                        aggregateLevel,
                        start: moduleStartTime,
                        end: moduleEndTime,
                        tableColumns: [
                            {
                                type: "stream",
                                path,
                                streamName,
                                streamType,
                                aggregateType,
                            } as IStreamColumn,
                        ],
                        timeDimension: true,
                        groupByDevice: true,
                        type: "stream",
                    } as ISqlQuery);
                } else {
                    result = {
                        rows: [],
                        columns: [],
                        rowCount: 0,
                        sqlText: "",
                        metadata: {
                            start: moduleStartTime,
                            end: moduleEndTime,
                            aggregateLevel,
                        },
                    };
                }

                if (stop) {
                    return;
                }

                _.override = [
                    {
                        value: {
                            ...result,
                            metadata: {
                                start: moduleStartTime,
                                end: moduleEndTime,
                                aggregateLevel,
                            },
                        },
                    },
                ];
            }

            // SQL NODES
            const sqlNodes = enabledSourceNodes.filter(n => n.type === "sql");

            const sqlNameToSources: { [key in string]: INode[] } = {};

            sqlNodes.forEach(_ => {
                const query = _.getAttribute("query") as string;
                if (!query && typeof query !== "string") {
                    throw new Error("SQL node missing query attribute");
                }
                if (!sqlNameToSources[query]) {
                    sqlNameToSources[query] = [];
                }
                sqlNameToSources[query].push(_);
            });

            await Promise.all(
                Object.entries(sqlNameToSources).map(([query, nodes]) =>
                    (async () => {
                        let data: ISqlResult & {
                            metadata?: {
                                start: string;
                                end: string;
                                aggregateLevel: string;
                            };
                        };
                        if (canDoRequest) {
                            data = await this.sqlQuery({
                                sqlQuery: query,
                                deviceIds: deviceIds.length
                                    ? deviceIds
                                    : undefined,
                                start: moduleStartTime,
                                aggregateLevel,
                                end: moduleEndTime,
                            });
                        } else {
                            data = {
                                rows: [],
                                columns: [],
                                rowCount: 0,
                                sqlText: "",
                                metadata: {
                                    start: moduleStartTime,
                                    end: moduleEndTime,
                                    aggregateLevel,
                                },
                            };
                        }

                        nodes.forEach(node => {
                            node.override = [
                                {
                                    value: defined({
                                        ...data,
                                        metadata: {
                                            start: moduleStartTime,
                                            end: moduleEndTime,
                                            aggregateLevel,
                                        },
                                    }),
                                },
                            ];
                        });

                        if (stop) {
                            return;
                        }
                    })(),
                ),
            );

            const httpIntegrations = enabledSourceNodes.filter(
                n => n.type === "http-integration",
            );
            const httpIntegrationNameToSources: {
                [key in string]: INode[];
            } = {};
            httpIntegrations.forEach(_ => {
                const httpIntegrationName = _.getAttribute("id") as string;
                if (
                    !httpIntegrationName &&
                    typeof httpIntegrationName !== "string"
                ) {
                    throw new Error(
                        "HTTP integration node missing id attribute",
                    );
                }
                if (!httpIntegrationNameToSources[httpIntegrationName]) {
                    httpIntegrationNameToSources[httpIntegrationName] = [];
                }
                httpIntegrationNameToSources[httpIntegrationName].push(_);
            });

            await Promise.all(
                Object.entries(httpIntegrationNameToSources).map(
                    ([id, nodes]) =>
                        (async () => {
                            let integrationResult:
                                | object
                                | "pending"
                                | undefined = integrationCache.get(id);
                            if (integrationResult === "pending") {
                                // wait til cache has something
                                while (integrationResult === "pending") {
                                    await new Promise(resolve =>
                                        window.setTimeout(resolve, 100),
                                    );
                                    integrationResult =
                                        integrationCache.get(id);
                                }
                            }
                            if (!integrationResult) {
                                integrationCache.set(id, "pending");
                                integrationResult =
                                    await integrationClient.executeGetHttpIntegration(
                                        await authStore.token(),
                                        id,
                                    );
                                integrationCache.set(id, integrationResult);
                            }
                            if (!integrationResult) {
                                throw new Error(
                                    "Integration result is undefined",
                                );
                            }

                            nodes.forEach(node => {
                                node.override = [
                                    {
                                        value: integrationResult,
                                    },
                                ];
                            });
                        })(),
                ),
            );

            let tagsForRequest = tags;
            if (this.processingGlobalFilter?.tags) {
                if (Array.isArray(tags)) {
                    tagsForRequest = tags.concat(
                        this.processingGlobalFilter.tags,
                    );
                } else {
                    tagsForRequest = {
                        ...tags,
                        ...this.processingGlobalFilter.tags,
                    };
                }
            }

            const aggregateableStreamTypes = [
                "numeric",
                "numeric set",
                "battery",
                "bitset",
            ];

            const sources = enabledSourceNodes.filter(
                n => n.type === "device-stream",
            );
            const streamNameToSources: { [key in string]: INode[] } = {};
            const streamNameToLatestValueSources: { [key in string]: INode[] } =
                {};
            const streamNameToAggSources: { [key in string]: INode[] } = {};
            sources.forEach(_ => {
                const streamName = _.getAttribute("streamName") as string;
                const streamType = _.getAttribute("streamType") as string;
                const showLatestValue = _.getOptionalAttribute(
                    "showLatestValue",
                ) as boolean;

                if (!streamName && typeof streamName !== "string") {
                    throw new Error(
                        "Device stream node missing stream attribute",
                    );
                }

                if (
                    aggregateableStreamTypes.includes(streamType) &&
                    !!aggregateSpan &&
                    !showLatestValue
                ) {
                    if (!streamNameToAggSources[streamName]) {
                        streamNameToAggSources[streamName] = [];
                    }
                    streamNameToAggSources[streamName].push(_);
                    return;
                }

                if (showLatestValue) {
                    if (!streamNameToLatestValueSources[streamName]) {
                        streamNameToLatestValueSources[streamName] = [];
                    }
                    streamNameToLatestValueSources[streamName].push(_);
                    return;
                }

                if (!streamNameToSources[streamName]) {
                    streamNameToSources[streamName] = [];
                }
                streamNameToSources[streamName].push(_);
            });

            const streamNames = Object.keys(streamNameToSources);
            const allStreamTypes = new Set(
                Object.entries(streamNameToSources).flatMap(([_, nodes]) =>
                    nodes.map(n => n.getAttribute("streamType") as StreamType),
                ),
            );

            if (streamNames.length) {
                let response: IStreamData[] = [];
                if (canDoRequest) {
                    response = await queryStore.queryAsync({
                        start: start.toISOString(),
                        end: end.toISOString(),
                        tags: tagsForRequest,
                        deviceIds: deviceIds.length ? deviceIds : undefined,
                        types:
                            this.processingGlobalFilter?.types ||
                            Array.from(allStreamTypes),
                        names: !this.processingGlobalFilter?.names
                            ? streamNames
                            : streamNames.filter(name =>
                                  this.processingGlobalFilter?.names?.includes(
                                      name,
                                  ),
                              ),
                    });
                }

                if (stop) {
                    return;
                }

                if (maxPointsPerStream) {
                    Object.entries(streamNameToSources).forEach(
                        ([streamName, nodes]) => {
                            const existInAggregateableStreamTypes = nodes.find(
                                n =>
                                    aggregateableStreamTypes.includes(
                                        n.getAttribute("streamType") as string,
                                    ),
                            );
                            if (!existInAggregateableStreamTypes) {
                                return;
                            }
                            const itemResponse = response.find(
                                r => r.name === streamName,
                            );
                            if (!itemResponse) {
                                return;
                            }

                            const hasHighVolume =
                                itemResponse.points.length > maxPointsPerStream;

                            if (hasHighVolume) {
                                if (!streamNameToAggSources[streamName]) {
                                    streamNameToAggSources[streamName] = [];
                                }
                                streamNameToAggSources[streamName] = [
                                    ...streamNameToAggSources[streamName],
                                    ...nodes,
                                ];
                                delete streamNameToSources[streamName];
                            }
                        },
                    );
                }

                Object.entries(streamNameToSources).forEach(
                    ([streamName, nodes]) => {
                        const streamData = response.filter(
                            x => x.name === streamName,
                        );
                        if (response.length && !streamData.length) {
                            deviceIds.forEach(deviceId => {
                                streamData.push({
                                    name: streamName,
                                    tags: {},
                                    type: "numeric",
                                    points: [],
                                    deviceId,
                                });
                            });
                        }

                        const deviceData = streamData.map(l => {
                            return {
                                deviceId: l.deviceId,
                                timeSeriesData: l.points.map(p => ({
                                    time: p[0],
                                    value: p[1],
                                })),
                                tags: l.tags,
                                name: l.name,
                                type: l.type,
                            };
                        });
                        const output = deviceData.map(l => ({
                            value: l,
                        }));
                        nodes.forEach(node => {
                            node.override = output;
                        });
                    },
                );
            }

            const streamLatestValueNames = Object.keys(
                streamNameToLatestValueSources,
            );

            if (streamLatestValueNames.length) {
                const startDateForLatestValue = new Date(
                    new Date().getTime() - 365 * 24 * 60 * 60 * 1000, // 1 year ago
                );
                const endDateForLatestValue = currentTime || new Date();
                let response: IMetadataWithCurrentValue[] = [];
                if (canDoRequest) {
                    response =
                        await metadataStore.listMetadataWithCurrentValueAsync({
                            start: startDateForLatestValue.toISOString(),
                            end: endDateForLatestValue.toISOString(),
                            tags: tagsForRequest,
                            deviceIds: deviceIds.length ? deviceIds : undefined,
                            types: this.processingGlobalFilter?.types,
                            names: !this.processingGlobalFilter?.names
                                ? streamLatestValueNames
                                : streamLatestValueNames.filter(name =>
                                      this.processingGlobalFilter?.names?.includes(
                                          name,
                                      ),
                                  ),
                        });
                }
                if (stop) {
                    return;
                }
                Object.entries(streamNameToLatestValueSources).forEach(
                    ([streamName, nodes]) => {
                        const streamData = response.filter(
                            x => x.name === streamName,
                        );

                        const deviceData = streamData.map(l => {
                            // Use the actual currentValueTime when available so
                            // modules (like the v2 map) can reason about
                            // online/offline purely from timestamps, similar to v1.
                            let timeSource =
                                l.currentValueTime || endDateForLatestValue;

                            if (
                                typeof timeSource === "string" &&
                                !timeSource.includes("Z")
                            ) {
                                timeSource += "Z";
                            }
                            return {
                                deviceId: l.deviceId,
                                timeSeriesData: [
                                    {
                                        time: new Date(timeSource).getTime(),
                                        value: l.currentValue,
                                    },
                                ],
                                tags: l.tags,
                                name: l.name,
                                type: l.type,
                            };
                        });
                        if (response.length && !streamData.length) {
                            deviceIds.forEach(deviceId => {
                                deviceData.push({
                                    name: streamName,
                                    tags: {},
                                    type: "numeric",
                                    timeSeriesData: [],
                                    deviceId,
                                });
                            });
                        }
                        const output = deviceData.map(l => ({
                            value: l,
                        }));
                        nodes.forEach(node => {
                            node.override = output;
                        });
                    },
                );
            }

            const streamAggregateNames = Object.keys(streamNameToAggSources);

            if (streamAggregateNames.length) {
                let response: IStreamAggregateData[] = [];
                if (canDoRequest) {
                    response = await queryStore.queryAggregatesAsync({
                        start: start.toISOString(),
                        end: end.toISOString(),
                        aggregate: aggregateSpan || getDefaultAggregateSpan(),
                        deviceIds: deviceIds.length ? deviceIds : undefined,
                        types: this.processingGlobalFilter?.types,
                        tags: tagsForRequest,
                        names: !this.processingGlobalFilter?.names
                            ? streamAggregateNames
                            : streamAggregateNames.filter(name =>
                                  this.processingGlobalFilter?.names?.includes(
                                      name,
                                  ),
                              ),
                    });
                }

                if (stop) {
                    return;
                }

                Object.entries(streamNameToAggSources).forEach(
                    ([streamName, nodes]) => {
                        const streamData = response.filter(
                            x => x.name === streamName,
                        );

                        if (response.length && !streamData.length) {
                            deviceIds.forEach(deviceId => {
                                streamData.push({
                                    name: streamName,
                                    tags: {},
                                    type: "numeric",
                                    aggregates: [],
                                    deviceId,
                                });
                            });
                        }

                        const queryAggregateData = streamData.map(l => {
                            if (l.type === "numeric") {
                                return {
                                    deviceId: l.deviceId,
                                    timeSeriesData: l.aggregates.map(p => {
                                        const value = p[1] as INumericAggregate;
                                        return {
                                            time: p[0],
                                            value: value.sum / value.count,
                                            min: value.min,
                                            max: value.max,
                                        };
                                    }),
                                    tags: l.tags,
                                    name: l.name,
                                    type: l.type,
                                };
                            } else if (l.type === "numeric set") {
                                return {
                                    deviceId: l.deviceId,
                                    timeSeriesData: l.aggregates.map(p => {
                                        const aggs = p[1] as any as {
                                            [key in string]: {
                                                value: INumericAggregate;
                                                unit?: string;
                                            };
                                        };
                                        const keys = Object.keys(aggs);

                                        const result = {
                                            time: p[0],
                                            value: keys.map(label => ({
                                                label,
                                                min: aggs[label].value.min,
                                                max: aggs[label].value.max,
                                                value:
                                                    aggs[label].value.sum /
                                                    aggs[label].value.count,
                                                unit: aggs[label].unit,
                                            })),
                                        };
                                        return result;
                                    }),
                                    tags: l.tags,
                                    name: l.name,
                                    type: l.type,
                                };
                            } else if (l.type === "battery") {
                                return {
                                    deviceId: l.deviceId,
                                    timeSeriesData: l.aggregates.map(p => {
                                        const agg = p[1] as IBatteryAggregate;
                                        const value: any = {};
                                        if (agg.percentage) {
                                            value.percentage = {
                                                min: agg.percentage.min,
                                                max: agg.percentage.max,
                                                value:
                                                    agg.percentage.sum /
                                                    agg.percentage.count,
                                            };
                                        }
                                        if (agg.voltage) {
                                            value.voltage = {
                                                min: agg.voltage.min,
                                                max: agg.voltage.max,
                                                value:
                                                    agg.voltage.sum /
                                                    agg.voltage.count,
                                            };
                                        }
                                        if (agg.current) {
                                            value.current = {
                                                min: agg.current.min,
                                                max: agg.current.max,
                                                value:
                                                    agg.current.sum /
                                                    agg.current.count,
                                            };
                                        }
                                        const result = {
                                            time: p[0],
                                            value,
                                        };
                                        return result;
                                    }),
                                    tags: l.tags,
                                    name: l.name,
                                    type: l.type,
                                };
                            } else if (l.type === "bitset") {
                                return {
                                    deviceId: l.deviceId,
                                    timeSeriesData: l.aggregates.map(p => {
                                        const agg =
                                            p[1] as IBitsetAggregate as any;
                                        const result = {
                                            time: p[0],
                                            value: {
                                                keys: Object.keys(agg),
                                                values: Object.keys(agg).map(
                                                    key => {
                                                        const value = agg[key];

                                                        const trueCount =
                                                            value?.true || 0;
                                                        const falseCount =
                                                            value?.false || 0;
                                                        const total =
                                                            trueCount +
                                                            falseCount;
                                                        const average =
                                                            total > 0
                                                                ? trueCount /
                                                                  total
                                                                : 0;
                                                        return average;
                                                    },
                                                ),
                                                aggregateData: Object.keys(
                                                    agg,
                                                ).map(key => {
                                                    const value = agg[key];

                                                    const trueCount =
                                                        value?.true || 0;
                                                    const falseCount =
                                                        value?.false || 0;
                                                    return {
                                                        key,
                                                        trueCount,
                                                        falseCount,
                                                    };
                                                }),
                                            },
                                        };
                                        return result;
                                    }),
                                    tags: l.tags,
                                    name: l.name,
                                    type: l.type,
                                };
                            } else {
                                return {
                                    deviceId: l.deviceId,
                                    timeSeriesData: [],
                                    tags: l.tags,
                                    name: l.name,
                                    type: l.type,
                                };
                            }
                        });

                        const output = queryAggregateData.map(l => ({
                            value: l,
                        }));
                        nodes.forEach(node => {
                            node.override = output;
                        });
                    },
                );
            }

            if (forceProcess || graph.shouldProcessGraph()) {
                const o = await graph.processGraph(graphContext);
                if (stop) {
                    return;
                }
                await subscriber(
                    o,
                    currentTime,
                    this.processingIsPlaying,
                    this.processingIsLive,
                );
            }

            if (!this.processingIsPlaying) {
                paused = true;
            }
        };

        // interval that gets called immediately

        const loop = async () => {
            if (stop) {
                return;
            }

            await process(firstTime);
            firstTime = false;
            window.setTimeout(loop, 1000);
        };
        fork(loop());

        return () => {
            stop = true;
            if (isRealtimeGraph) {
                this.registeredRealtimeNodeGraphs =
                    this.registeredRealtimeNodeGraphs.filter(g => g !== graph);
                this.deinitializeGraph();
            }
        };
    }

    async initializeRealtimeGraph() {
        await this.handleAuthentication();

        const ctx = this.deviceContext;

        if (!ctx) {
            return;
        }

        if (ctx.type === "all") {
            console.log("Realtime doesn't work in context all");
        }

        // TODO: support multiple devices
        const deviceId = ctx.type === "all" ? undefined : ctx.deviceIds[0];

        let d: RealtimeDevice;
        if (deviceId) {
            const token = await authStore.token();
            d = await this.realtimeManager.getRealtimeDevice(deviceId, token);
        } else {
            return;
        }

        this.registeredRealtimeNodeGraphs.forEach(graph => {
            // find all nodes of type "realtime-stream"
            const realtimeNodes = graph.sourceNodes.filter(
                n => n.type === "realtime-stream",
            );

            realtimeNodes.forEach(async _ => {
                _.override = [];
                const streamName = _.getAttribute("streamName") as string;
                if (!streamName && typeof streamName !== "string") {
                    throw new Error(
                        "Realtime stream node missing stream attribute",
                    );
                }
                const streamType = _.getAttribute("streamType") as string;
                if (!streamType && typeof streamType !== "string") {
                    throw new Error(
                        "Realtime stream node missing stream type attribute",
                    );
                }

                console.log("Subscribing to", streamName, streamType);
                const cb: RealtimeListener = async (
                    _peerid: string,
                    message: RealtimeMessage,
                ) => {
                    const keys = Object.keys(message.payload);
                    const firstKey = keys[0];
                    const obj = message.payload as {
                        [key in string]: object;
                    };

                    _.override = [
                        {
                            value: {
                                ...(obj[firstKey] as object),
                                ...{ time: message.header.created },
                                ...{ deviceId },
                            },
                        },
                    ];
                    _.dirty = true;

                    // canvas nodes must be rendered as soon as new frames arrive
                    const outputCanvasNodes = graph.outputNodes.filter(
                        n => n.type === "ui-canvas",
                    );
                    for (let i = 0; i < outputCanvasNodes.length; i++) {
                        const parentNodes = graph.getParentNodes(
                            outputCanvasNodes[i],
                        );
                        const foundCurrentNode = parentNodes.filter(
                            n => n.id === _.id,
                        );
                        if (!foundCurrentNode) {
                            continue;
                        } else {
                            const parentFrameToCanvas = parentNodes.filter(
                                n => n.type === "h264-frame-to-canvas",
                            );
                            if (parentFrameToCanvas.length === 1) {
                                await parentFrameToCanvas[0].process(
                                    _.override,
                                );
                            }
                            break;
                        }
                    }
                };
                d.subscribeToRealtimeStream(streamName, cb);
            });
        });

        if (d.hasSubscribedToRealtimeStreams()) {
            d.start();
        }
    }

    private async handleAuthentication() {
        this.initialAuth = true;
        await Authentication.loginWithToken(await authStore.token());
    }

    deinitializeGraph() {
        const ctx = this.deviceContext;
        if (!ctx) {
            return;
        }
        const deviceId = ctx.type === "all" ? undefined : ctx.deviceIds[0];
        if (deviceId) {
            this.realtimeManager.stopRealtimeDevice(deviceId);
        }
    }

    sendCommand(
        _deviceId: string,
        _command: string,
        _paramValue: any,
    ): Promise<void> {
        throw new Error("Method not implemented.");
    }

    async experimentalExecuteWorkflow(
        _graph: INodeGraph,
        _inputs: { [key: string]: NodeInputValueSet[] } | undefined,
        _deviceContext: IModuleDeviceContext,
        _time: IModuleTime,
        _preview: boolean,
    ): Promise<{ outputs: INodeGraphOutput[]; actions: IWorkflowAction[] }> {
        return {
            outputs: [],
            actions: [],
        };
    }

    async setKeyValue(key: string, value: string, tags?: ITags): Promise<void> {
        const token = await authStore.token();
        await keyValueClient.storeItem(token, key, value, tags);
    }

    async getKeyValue(key: string): Promise<string | undefined> {
        const token = await authStore.token();
        return await keyValueClient.getItem(token, key);
    }

    async removeKeyValue(key: string): Promise<void> {
        const token = await authStore.token();
        await keyValueClient.removeItem(token, key);
    }

    async queryKeyValues(param: {
        prefix: string;
    }): Promise<{ key: string; value: string }[]> {
        const token = await authStore.token();
        return await keyValueClient.query(token, param);
    }

    async getCurrentUser(): Promise<IUser> {
        const user = userStore.authenticated();
        if (!user) {
            throw new Error("User not found");
        }
        return user;
    }

    async listUsers(): Promise<IUser[]> {
        return userStore.listAllAsync();
    }

    async updateInterventionRequest(
        id: Uuid,
        updates: Partial<IInterventionRequest>,
    ): Promise<IInterventionRequest> {
        const token = await authStore.token();
        return await interventionRequestClient.update(token, id, updates);
    }

    async createInterventionResponse(
        response: IInterventionResponse,
    ): Promise<IInterventionResponse> {
        const token = await authStore.token();
        return await interventionResponseClient.create(token, response);
    }

    async describeImage(_canvas: HTMLCanvasElement): Promise<string> {
        throw new Error("Image description is not available.");
    }

    experimental = {
        commandStore,
        customModuleStore,
        deviceStore,
        queryStore,
        metadataStore,
        eventStore,
        streamCurrentValueStore,
        explorationStore: {} as any,
    };

    async getSeekResultByDataPresence(query: ISeekQuery): Promise<ISeekResult> {
        return await seekClient.seek(await authStore.token(), query);
    }

    async getSeekResultByEvent(
        query: IEventSeekQuery,
    ): Promise<IEvent | undefined> {
        return await eventClient.seek(await authStore.token(), query);
    }

    async getUser(userId: string): Promise<IUser | undefined> {
        return (await userStore.fetchGet(userId)) || undefined;
    }

    async listCustomModules(): Promise<DefinedEntity<IModule>[] | undefined> {
        const res = await customModuleStore.listAllAsync();
        return res;
    }

    async updateSimple(
        id: Uuid,
        view: Partial<IView>,
    ): Promise<Partial<IView>> {
        const res = await viewStore.updateSimple(id, view);
        return res;
    }

    async queryPresence(
        data: IIntervalQuery,
    ): Promise<IPresenceInterval[] | undefined> {
        return presenceStore.query(data);
    }

    async tags(filter: IDeviceQuery) {
        return await deviceStore.tagsAsync({
            tags: filter.tags,
            enabled: true,
            type: "default",
        });
    }

    public async fetchCountsByDevice(
        filter: IEventFilter,
    ): Promise<IEventCountsByDevice | undefined> {
        const value = await eventStore.fetchCountsByDevice(filter);
        return value;
    }

    async askDocumentSets(
        input: string,
        selectedDocumentSets: string[],
    ): Promise<IWorkflowWebRagResponse> {
        return await documentSetStore.queryDocumentSets(
            input,
            selectedDocumentSets,
        );
    }

    async getDocumentSets(): Promise<HasId<IDocumentSet>[]> {
        return await documentSetStore.getDocumentSets();
    }

    async createCommentEvent(request: IComment): Promise<IComment> {
        return await commentStore.create(request);
    }

    async deleteComment(commentId: Uuid): Promise<void> {
        await commentStore.delete(commentId);
    }

    async createSystemEvent(request: ISystemEvent): Promise<ISystemEvent> {
        const token = await authStore.token();
        return await systemEventClient.create(token, request);
    }

    async fetchCommandTemplates(): Promise<ICommandTemplate[]> {
        return await commandTemplateStore.fetchList();
    }

    async getDevicesDetails(deviceIds: string[]): Promise<IDevice[]> {
        return await deviceStore.fetchList({ deviceIds });
    }

    async fetchCommands(deviceId: Uuid): Promise<ICommand[]> {
        return (await commandStore.fetchList(deviceId)) as ICommand[];
    }

    async fetchCommand(commandId: Uuid): Promise<ICommand | undefined> {
        return await commandStore.fetch(commandId);
    }

    async fetchCommandTemplate(
        commandTemplateId: Uuid,
    ): Promise<ICommandTemplate | false> {
        return await commandTemplateStore.fetchGet(commandTemplateId);
    }

    async createCommand(command: ICommand): Promise<ICommand> {
        const organizationId = await authStore.organizationId;
        if (!organizationId) {
            throw new Error("Organization ID not found");
        }
        command.organizationId = organizationId;
        return await commandStore.create(command);
    }

    async fetchAnnotationTemplate(
        annotationTemplateId: Uuid,
    ): Promise<IAnnotationTemplate | false> {
        return await annotationTemplateStore.fetchGet(annotationTemplateId);
    }

    async deleteAnnotation(annotationId: Uuid): Promise<void> {
        await annotationStore.delete(annotationId);
    }

    async saveAnnotation(annotation: IAnnotation): Promise<IAnnotation> {
        return await annotationStore.save(annotation);
    }

    async listAnnotationTemplatesMatching(
        tags: ITags,
    ): Promise<DefinedEntity<IAnnotationTemplate>[] | undefined> {
        return await annotationTemplateStore.fetchListMatching(tags);
    }

    async listAnnotationTemplates(): Promise<
        DefinedEntity<IAnnotationTemplate>[] | undefined
    > {
        return (await annotationTemplateStore.fetchList()).filter(
            _ => _.enabled,
        );
    }

    async getTheopolisTasks(): Promise<IListResponse<ITheopolisTask>> {
        const token = await authStore.token();
        return await theopolisClient.getTheopolisTasks(token);
    }

    async getTheopolisTask(insightId: Uuid): Promise<ITheopolisTask> {
        const token = await authStore.token();
        return await theopolisClient.getTheopolisTask(token, insightId);
    }

    async createTheopolisTask(
        insight: ICreateTheopolisTaskRequest,
    ): Promise<ITheopolisTask> {
        const token = await authStore.token();
        return await theopolisClient.createTheopolisTask(token, insight);
    }

    async updateTheopolisTask(
        insightId: string,
        updates: IPatchTheopolisTaskRequest,
    ): Promise<ITheopolisTask> {
        const token = await authStore.token();
        return await theopolisClient.updateTheopolisTask(
            token,
            insightId,
            updates,
        );
    }

    async deleteTheopolisTask(insightId: string): Promise<void> {
        const token = await authStore.token();
        return await theopolisClient.deleteTheopolisTask(token, insightId);
    }

    async getTheopolisTaskResults(
        insightId: Uuid,
    ): Promise<IListResponse<ITheopolisTaskResult>> {
        const token = await authStore.token();
        return await theopolisClient.getTheopolisTaskResults(token, insightId);
    }

    async createTheopolisTaskResult(
        insightId: string,
        result: ICreateTheopolisTaskResultRequest,
    ): Promise<ITheopolisTaskResult> {
        const token = await authStore.token();
        return await theopolisClient.createTheopolisTaskResult(
            token,
            insightId,
            result,
        );
    }

    async getTheopolisTaskFeedback(
        insightId: Uuid,
    ): Promise<IListResponse<ITheopolisTaskResultFeedback>> {
        const token = await authStore.token();
        return await theopolisClient.getTheopolisTaskFeedback(token, insightId);
    }

    // Required by IModuleClient (@formant/rectangle-sdk 1.11.0) but called by nothing.
    // Kept only to satisfy the published interface, which can no longer be revised.
    async getWorkflowShallow(): Promise<
        IListResponse<IWorkflowWebWorkflowShallow>
    > {
        return await workflowClient.getListShallow(await authStore.token());
    }

    async createTheopolisTaskPromptVersionFeedback(
        insightId: string,
        promptVersionId: string,
        feedback: ICreateTheopolisTaskResultFeedbackRequest,
    ): Promise<ITheopolisTaskResultFeedback> {
        const token = await authStore.token();
        return await theopolisClient.createTheopolisTaskPromptVersionFeedback(
            token,
            insightId,
            promptVersionId,
            feedback,
        );
    }

    async queueTheopolisTaskSource(id: string): Promise<void> {
        const token = await authStore.token();
        return await theopolisClient.queueTheopolisTaskSource(token, id);
    }

    async queueTheopolisTask(id: string): Promise<void> {
        const token = await authStore.token();
        return await theopolisClient.queueTheopolisTask(token, id);
    }

    async createTheopolisTaskSource(
        request: ICreateTheopolisTaskSourceRequest,
    ): Promise<ITheopolisTaskSource> {
        const token = await authStore.token();
        return await theopolisClient.createTheopolisTaskSource(token, request);
    }

    async getTheopolisTaskSource(id: string): Promise<ITheopolisTaskSource> {
        const token = await authStore.token();
        return await theopolisClient.getTheopolisTaskSource(token, id);
    }

    async updateTheopolisTaskSource(
        id: string,
        updates: IUpdateTheopolisTaskSourceRequest,
    ): Promise<ITheopolisTaskSource> {
        const token = await authStore.token();
        return await theopolisClient.updateTheopolisTaskSource(
            token,
            id,
            updates,
        );
    }

    async deleteTheopolisTaskSource(id: string): Promise<void> {
        const token = await authStore.token();
        return await theopolisClient.deleteTheopolisTaskSource(token, id);
    }

    async listOldPersonas(): Promise<IListResponse<IPersona>> {
        const token = await authStore.token();
        return await theopolisClient.listPersonas(token);
    }

    async listNewPersonas() {
        const token = await authStore.token();
        // TypeScript resolves IModuleClient through @formant/client-query's nested @formant/client,
        // but we use @formant/core. The types are compatible, so we cast to satisfy TypeScript.
        return (await gatewayClient.listOrgPersonas(
            token,
        )) as unknown as Awaited<ReturnType<IModuleClient["listNewPersonas"]>>;
    }

    async updateTheopolisTaskFeedback(
        insightId: string,
        feedbackId: string,
        updates: IPatchTheopolisTaskResultFeedbackRequest,
    ): Promise<ITheopolisTaskResultFeedback> {
        const token = await authStore.token();
        return await theopolisClient.updateTheopolisTaskFeedback(
            token,
            insightId,
            feedbackId,
            updates,
        );
    }

    async generateStructure(
        modelId: ReasoningModels,
        messages: CoreSystemMessage[],
        schema: object,
        reasoningEffort?: ReasoningEffort,
    ): Promise<AiStructureGenerationResponse> {
        const token = await authStore.token();
        return await aiClient.generateStructure(
            token,
            modelId,
            messages,
            schema as JSONSchema7Object,
            reasoningEffort,
        );
    }

    async saveSchedule(schedule: ISchedule): Promise<ISchedule> {
        return await scheduleStore.save(schedule);
    }

    async executeWorkflowWebhook(
        workflowId: string,
        params: object,
    ): Promise<void> {
        const token = await authStore.token();
        await workflowClient.executeWorkflowWebhook(token, workflowId, params);
    }

    async getGatewayAuthToken(gatewayId: string): Promise<IGatewayToken> {
        const token = await authStore.token();

        const response = await gatewayClient.getGatewayAuthToken(
            token,
            gatewayId,
        );
        return response;
    }

    async clearGatewayAuthToken(gatewayId: string): Promise<void> {
        const token = await authStore.token();
        await gatewayClient.clearToken(token, gatewayId);
    }

    async keyringLogin(login: IKeyringLogin): Promise<IKeyringLoginResponse> {
        const token = await authStore.token();
        return await keyringClient.login(token, login);
    }

    async keyringLogout(logout: IKeyringLogout): Promise<void> {
        const token = await authStore.token();
        await keyringClient.logout(token, logout);
    }

    async keyringQuery(
        filter: IKeyringFilter,
    ): Promise<IListResponse<IKeyring>> {
        const token = await authStore.token();
        return await keyringClient.query(token, filter);
    }

    async integrationQuery(): Promise<IListResponse<IIntegration>> {
        const token = await authStore.token();
        return await integrationClient.listAll(token);
    }
}
